import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  schema,
}: SEOProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://otorobots.com';

    // Update title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]');
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.setAttribute('rel', 'canonical');
        document.head.appendChild(linkElement);
      }
      linkElement.setAttribute('href', `${siteUrl}${canonical}`);
    }

    // Update Open Graph tags
    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    if (canonical) {
      updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    // Update Twitter Card tags
    if (ogTitle) {
      updateMetaTag('twitter:title', ogTitle);
    }

    if (ogDescription) {
      updateMetaTag('twitter:description', ogDescription);
    }

    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptElement = document.querySelector('script[data-schema="page"]');
      
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.setAttribute('type', 'application/ld+json');
        scriptElement.setAttribute('data-schema', 'page');
        document.head.appendChild(scriptElement);
      }

      const schemaWithContext = {
        '@context': 'https://schema.org',
        url: canonical ? `${siteUrl}${canonical}` : siteUrl,
        ...schema,
      };

      scriptElement.textContent = JSON.stringify(schemaWithContext);
    }

    // Cleanup function
    return () => {
      const pageSchema = document.querySelector('script[data-schema="page"]');
      if (pageSchema) {
        pageSchema.remove();
      }
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogImage, schema]);
}
